import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { getRupeeWithDecimal, twoDecimalNumber } from '../../utils/numbers';
import Input from '../../components/Fields/Input';
import { EditIcon } from '../../assets/mui';

const STEP = 0.01;
const DEFAULT_FLOOR = 0.5;

export type DownSellPreset = {
  id?: string | number;
  meta?: { targetYieldPct?: number | string };
  targetYieldPct?: number | string;
};

type DownSellYieldScrubberProps = {
  dealYield: number;
  value: string;
  onChange: (next: string) => void;
  onDragStateChange?: (dragging: boolean) => void;
  presets?: DownSellPreset[];
  profit?: number | null;
  grossProfit?: number | null;
  sharePct?: number | null;
  retained?: number | null;
  isUpdating?: boolean;
};

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function roundToStep(n: number) {
  return Math.round(n / STEP) * STEP;
}

function parseYield(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatYield(n: number) {
  return String(Number(twoDecimalNumber(n)));
}

export function getDownSellMinYield(dealYield: number, presets: DownSellPreset[] = []): number {
  const presetFloor = presets.reduce<number | null>((acc, row) => {
    const y = parseYield(row?.meta?.targetYieldPct ?? row?.targetYieldPct);
    if (y == null) return acc;
    return acc == null ? y : Math.min(acc, y);
  }, null);

  const floor = presetFloor != null ? Math.min(DEFAULT_FLOOR, presetFloor) : DEFAULT_FLOOR;
  const maxAllowed = Math.max(STEP, dealYield - STEP);
  return clamp(roundToStep(floor), STEP, maxAllowed);
}

const DownSellYieldScrubber: React.FC<DownSellYieldScrubberProps> = ({
  dealYield,
  value,
  onChange,
  onDragStateChange,
  presets = [],
  profit,
  grossProfit,
  sharePct,
  retained,
  isUpdating = false,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const yieldInputRef = useRef<HTMLInputElement>(null);
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingYield, setIsEditingYield] = useState(false);

  const minYield = useMemo(() => getDownSellMinYield(dealYield, presets), [dealYield, presets]);
  const maxYield = useMemo(
    () => Math.max(minYield, roundToStep(dealYield - STEP)),
    [dealYield, minYield],
  );

  const clientYield = useMemo(() => {
    const parsed = parseYield(value);
    if (parsed == null) return maxYield;
    return clamp(roundToStep(parsed), minYield, maxYield);
  }, [value, minYield, maxYield]);

  const range = Math.max(STEP, maxYield - minYield);
  const clientRatio = clamp((clientYield - minYield) / range, 0, 1);
  const spread = Math.max(0, roundToStep(dealYield - clientYield));

  const commitYield = useCallback(
    (next: number, { fromDrag = false }: { fromDrag?: boolean } = {}) => {
      const clamped = clamp(roundToStep(next), minYield, maxYield);
      onChange(formatYield(clamped));
      if (!fromDrag) onDragStateChange?.(false);
    },
    [minYield, maxYield, onChange, onDragStateChange],
  );

  const yieldFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return clientYield;
      const rect = el.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / Math.max(rect.width, 1), 0, 1);
      return minYield + ratio * range;
    },
    [clientYield, minYield, range],
  );

  const setDragging = useCallback(
    (next: boolean) => {
      draggingRef.current = next;
      setIsDragging(next);
      onDragStateChange?.(next);
    },
    [onDragStateChange],
  );

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (!draggingRef.current) return;
      event.preventDefault();
      commitYield(yieldFromClientX(event.clientX), { fromDrag: true });
    };

    const onPointerUp = () => {
      if (!draggingRef.current) return;
      setDragging(false);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [commitYield, setDragging, yieldFromClientX]);

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
    commitYield(yieldFromClientX(event.clientX), { fromDrag: true });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      commitYield(clientYield - STEP);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      commitYield(clientYield + STEP);
    } else if (event.key === 'Home') {
      event.preventDefault();
      commitYield(minYield);
    } else if (event.key === 'End') {
      event.preventDefault();
      commitYield(maxYield);
    }
  };

  const presetYields = useMemo(() => {
    return presets
      .map((row) => {
        const yieldPct = parseYield(row?.meta?.targetYieldPct ?? row?.targetYieldPct);
        if (yieldPct == null) return null;
        return { id: row.id ?? String(yieldPct), yieldPct };
      })
      .filter(Boolean) as { id: string | number; yieldPct: number }[];
  }, [presets]);

  return (
    <div className="mt-3 flex flex-col gap-2">
      <label className="text-xs uppercase tracking-wider text-black/40 font-semibold">
        Client yield % (must be below {formatYield(dealYield)})
      </label>

      <div className="relative">
        <Input
          ref={yieldInputRef}
          type="text"
          value={value}
          readOnly={!isEditingYield}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
          onFocus={() => setIsEditingYield(true)}
          onBlur={() => {
            const parsed = parseYield(value);
            commitYield(parsed == null ? maxYield : parsed);
            setIsEditingYield(false);
          }}
          className={`font-medium !pr-11 ${!isEditingYield ? 'cursor-pointer bg-stone-50' : ''}`}
          onClick={() => {
            if (!isEditingYield) {
              setIsEditingYield(true);
              window.setTimeout(() => yieldInputRef.current?.focus(), 0);
            }
          }}
        />
        <IconButton
          aria-label="edit"
          size="small"
          className="!absolute right-1.5 top-1/2 -translate-y-1/2"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setIsEditingYield(true);
            window.setTimeout(() => yieldInputRef.current?.focus(), 0);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Flat split scrubber — same green/stone language as the order panel */}
      <div className="pt-1">
        <div className="mb-1.5 flex items-center justify-between text-sm text-slate-600">
          <span>
            Client <span className="font-medium">{formatYield(clientYield)}%</span>
          </span>
          <span>
            Spread{' '}
            <span className="font-medium text-green-700">{formatYield(spread)}%</span>
          </span>
        </div>

        <div
          ref={trackRef}
          role="slider"
          tabIndex={0}
          aria-valuemin={minYield}
          aria-valuemax={maxYield}
          aria-valuenow={clientYield}
          aria-label="Client yield"
          className="relative h-8 touch-none select-none cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-green-700/30 rounded-full"
          onPointerDown={handleTrackPointerDown}
          onKeyDown={handleKeyDown}
        >
          <div className="absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 overflow-hidden rounded-full bg-stone-200">
            <div
              className="absolute inset-y-0 left-0 bg-stone-400"
              style={{ width: `${clientRatio * 100}%` }}
            />
            <div
              className="absolute inset-y-0 right-0 bg-green-700"
              style={{ width: `${(1 - clientRatio) * 100}%` }}
            />
          </div>
          <div
            className={`absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-green-700 shadow-sm ${
              isDragging ? 'ring-2 ring-green-700/20' : ''
            }`}
            style={{ left: `${clientRatio * 100}%` }}
          />
        </div>
      </div>

      {presetYields.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {presetYields.map((row) => {
            const active = Math.abs(row.yieldPct - clientYield) < STEP / 2;
            return (
              <button
                type="button"
                key={row.id}
                className={`text-xs rounded-full px-3 py-1 ${
                  active
                    ? 'border border-green-700 bg-green-700/10 text-green-800 font-semibold'
                    : 'bg-green-700/10 text-green-800'
                }`}
                onClick={() => commitYield(row.yieldPct)}
              >
                {formatYield(row.yieldPct)}%
              </button>
            );
          })}
        </div>
      )}

      {profit != null && (
        <div className="text-sm pt-0.5">
          <p className="text-green-700 font-medium">
            Your profit: {getRupeeWithDecimal.format(profit)}
            {isUpdating ? <span className="ml-1 text-xs font-normal text-stone-400">…</span> : null}
          </p>
          {(grossProfit != null || sharePct != null || retained != null) && (
            <p className="text-xs text-stone-500 mt-1">
              Gross spread:{' '}
              {grossProfit != null ? getRupeeWithDecimal.format(grossProfit) : '-'}
              {sharePct != null ? ` | Effective share: ${twoDecimalNumber(sharePct)}%` : ''}
              {retained != null
                ? ` | Retained: ${getRupeeWithDecimal.format(retained)}`
                : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DownSellYieldScrubber;
